import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { 
  Check, X, Zap, Crown, Rocket, ArrowLeft, Sparkles,
  Gamepad2, Box, Users, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const plans = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    period: 'forever',
    icon: Gamepad2,
    color: 'zinc',
    tagline: 'All the essentials. Forever free.',
    features: [
      { text: '15 game projects', included: true },
      { text: 'Full 3D editor & templates', included: true },
      { text: 'Community gallery — play & remix', included: true },
      { text: 'Publish games & share', included: true },
      { text: 'AI Copilot — 15 msgs/day', included: true },
      { text: 'AI game generation — 5/day', included: true },
      { text: 'AI asset generation — 5/day', included: true },
      { text: 'PlayMold watermark on exports', included: true },
    ],
    cta: 'Current Plan',
    popular: false,
  },
  {
    id: 'pro_monthly',
    tier: 'pro',
    name: 'Pro',
    price: 12,
    period: 'month',
    icon: Zap,
    color: 'cyan',
    tagline: 'For serious creators shipping regularly.',
    features: [
      { text: 'Unlimited projects', included: true },
      { text: 'Everything in Free', included: true },
      { text: 'AI Copilot — 300 msgs/day', included: true },
      { text: 'AI game generation — 100/day', included: true },
      { text: 'AI asset generation — 100/day', included: true },
      { text: 'No watermark, HD exports', included: true },
      { text: 'Featured Game eligibility', included: true },
      { text: 'Email support', included: true },
    ],
    cta: 'Subscribe',
    popular: true,
  },
  {
    id: 'studio_monthly',
    tier: 'studio',
    name: 'Studio',
    price: 39,
    period: 'month',
    icon: Crown,
    color: 'purple',
    tagline: 'For teams and power users.',
    features: [
      { text: 'Everything in Pro', included: true },
      { text: 'Effectively unlimited AI usage', included: true },
      { text: 'Team seats (3)', included: true },
      { text: 'Priority AI queue', included: true },
      { text: '4K exports', included: true },
      { text: 'Advanced asset generation', included: true },
      { text: 'Priority 24/7 support', included: true },
      { text: 'Custom domain', included: true },
    ],
    cta: 'Go Studio',
    popular: false,
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { t } = useI18n();
  const [loading, setLoading] = useState(null);

  const handleSubscribe = async (planId) => {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }

    if (planId === 'free') {
      toast.info('You are already on the Free plan');
      return;
    }

    setLoading(planId);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin,
      }, { withCredentials: true });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const colorClasses = {
    zinc: {
      bg: 'bg-zinc-900',
      border: 'border-zinc-700',
      icon: 'bg-zinc-800 text-zinc-400',
      button: 'bg-zinc-700 hover:bg-zinc-600 text-white',
    },
    cyan: {
      bg: 'bg-gradient-to-b from-[#06B6D4]/10 to-transparent',
      border: 'border-[#06B6D4]/50',
      icon: 'bg-[#06B6D4]/20 text-[#06B6D4]',
      button: 'bg-[#06B6D4] hover:bg-[#06B6D4]/90 text-black',
    },
    purple: {
      bg: 'bg-gradient-to-b from-purple-500/10 to-transparent',
      border: 'border-purple-500/50',
      icon: 'bg-purple-500/20 text-purple-400',
      button: 'bg-purple-500 hover:bg-purple-600 text-white',
    },
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="border-b border-[#1E1E24] bg-[#0A0A0C]/80 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#06B6D4] to-[#0891B2] flex items-center justify-center">
              <Gamepad2 className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-white">PlayMold</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#06B6D4]/10 border border-[#06B6D4]/30 mb-6">
              <Sparkles className="w-4 h-4 text-[#06B6D4]" />
              <span className="text-xs font-mono tracking-wider text-[#06B6D4]">PRICING</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
              Choose Your Plan
            </h1>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              <strong className="text-white">The editor, gallery, remix and publishing are free forever.</strong>
              &nbsp;Only high-cost AI features (Copilot, game &amp; asset generation) are metered — with a generous free daily allowance.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const colors = colorClasses[plan.color];
              const Icon = plan.icon;
              const isCurrentPlan = user?.tier === plan.id;
              
              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl border ${colors.border} ${colors.bg} p-6 flex flex-col`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#06B6D4] text-black text-xs font-bold rounded-full">
                      MOST POPULAR
                    </div>
                  )}

                  <div className={`w-12 h-12 rounded-xl ${colors.icon} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6" />
                  </div>

                  <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
                  {plan.tagline && <p className="text-xs text-zinc-500 mb-4">{plan.tagline}</p>}
                  
                  <div className="mb-6">
                    <span className="text-4xl font-black text-white">
                      ${plan.price}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-zinc-500">/{plan.period}</span>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        {feature.included ? (
                          <Check className="w-5 h-5 text-emerald-400 shrink-0" />
                        ) : (
                          <X className="w-5 h-5 text-zinc-600 shrink-0" />
                        )}
                        <span className={feature.included ? 'text-zinc-300' : 'text-zinc-600'}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full ${colors.button}`}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={loading === plan.id || isCurrentPlan}
                  >
                    {loading === plan.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isCurrentPlan ? (
                      'Current Plan'
                    ) : (
                      plan.cta
                    )}
                  </Button>
                </div>
              );
            })}
          </div>

          {/* FAQ Link */}
          <div className="text-center mt-12">
            <p className="text-zinc-500">
              Have questions?{' '}
              <button onClick={() => navigate('/faq')} className="text-[#06B6D4] hover:underline">
                Check our FAQ
              </button>
              {' '}or{' '}
              <button onClick={() => navigate('/contact')} className="text-[#06B6D4] hover:underline">
                contact support
              </button>
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
